/*
 * Dialog focus management for <x-drawer> (PRANA-161).
 *
 * Kept outside the webpack bundle on purpose: main.build.min.js has not been
 * rebuilt since 2024 and rebuilding it would ship unrelated source changes.
 * If x-drawer.ts is updated and the bundle rebuilt, move this logic there.
 *
 * - Names the dialog from the host's `label` attribute (the shadow template's
 *   aria-labelledby points at an id that doesn't exist).
 * - On open: remembers the trigger, makes the rest of the page inert and moves
 *   focus into the dialog.
 * - On close: restores the page and returns focus to the trigger.
 */
(function () {
	const SKIP_TAGS = ['SCRIPT', 'STYLE', 'LINK'];

	function setOutsideInert(host) {
		const changed = [];
		let node = host;

		while (node && node.parentElement && node !== document.body) {
			for (const sibling of node.parentElement.children) {
				if (sibling === node || sibling.inert || SKIP_TAGS.includes(sibling.tagName)) continue;
				sibling.inert = true;
				changed.push(sibling);
			}
			node = node.parentElement;
		}

		return changed;
	}

	// Third-party widgets (e.g. Olark chat) can inject into <body> after the
	// drawer opens; keep those out of the tab order too.
	function watchLateAdditions(host, changed) {
		const observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				for (const added of mutation.addedNodes) {
					if (added.nodeType !== 1 || added.inert || added.contains(host) || SKIP_TAGS.includes(added.tagName)) continue;
					added.inert = true;
					changed.push(added);
				}
			}
		});
		observer.observe(document.body, { childList: true });
		return observer;
	}

	function enhance(host) {
		if (host.__a11yEnhanced || !host.shadowRoot) return;
		const dialog = host.shadowRoot.querySelector('[role="dialog"]');
		if (!dialog) return;
		host.__a11yEnhanced = true;

		const label = host.getAttribute('label');
		if (!host.shadowRoot.getElementById(dialog.getAttribute('aria-labelledby'))) {
			dialog.removeAttribute('aria-labelledby');
		}
		if (label) dialog.setAttribute('aria-label', label);

		dialog.setAttribute('tabindex', '-1');
		dialog.style.outline = 'none';

		let returnFocusEl = null;
		let inertEls = [];
		let lateObserver = null;
		let isOpen = dialog.classList.contains('is-open');

		new MutationObserver(() => {
			const nowOpen = dialog.classList.contains('is-open');
			if (nowOpen === isOpen) return;
			isOpen = nowOpen;

			if (nowOpen) {
				const active = document.activeElement;
				returnFocusEl = active && active !== document.body && !host.contains(active) ? active : null;
				inertEls = setOutsideInert(host);
				lateObserver = watchLateAdditions(host, inertEls);
				dialog.focus({ preventScroll: true });
			} else {
				if (lateObserver) lateObserver.disconnect();
				lateObserver = null;
				inertEls.forEach((el) => (el.inert = false));
				inertEls = [];
				if (returnFocusEl && returnFocusEl.isConnected) {
					returnFocusEl.focus({ preventScroll: true });
				}
				returnFocusEl = null;
			}
		}).observe(dialog, { attributes: true, attributeFilter: ['class'] });
	}

	customElements.whenDefined('x-drawer').then(() => {
		document.querySelectorAll('x-drawer').forEach(enhance);
	});
})();
